import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Brain, Loader2, Check, X, FileSearch, OctagonAlert, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const STOP_OPTIONS = [10, 25, 50];

/** ASReview-style active-learning queue: always the most likely relevant record next. */
export default function SmartQueue({ userId, sessionId, question, onDecided }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState('');
  const poll = useRef(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setData(await thesisApi.smartQueue(userId, sessionId, 5));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [userId, sessionId]);

  useEffect(() => { load(); return () => clearInterval(poll.current); }, [load]);

  // while the cold-start AI seeding runs, refresh quietly
  useEffect(() => {
    clearInterval(poll.current);
    if (data?.ai_status === 'running') {
      poll.current = setInterval(() => load(true), 5000);
    }
    return () => clearInterval(poll.current);
  }, [data?.ai_status, load]);

  const decide = async (record, stage) => {
    setDeciding(record.id);
    try {
      await thesisApi.updateScreeningRecord(userId, record.id, { stage, reason: '' });
      await load(true);
      onDecided?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setDeciding(''); }
  };

  const setStopRule = async (n) => {
    try {
      await thesisApi.setScreeningStopRule(userId, sessionId, n);
      await load(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const s = data?.stats;
  const top = data?.queue?.[0];
  const rest = (data?.queue || []).slice(1, 4);

  const curve = s?.curve || [];
  const maxInc = Math.max(1, ...curve.map((p) => p.included));

  return (
    <div className="rounded-lg border border-[#1d4ed8]/30 bg-[#1d4ed8]/[0.03] p-3 space-y-3"
      data-testid="smart-queue">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-medium inline-flex items-center gap-1.5">
          <Brain className="w-4 h-4 text-[#1d4ed8]" /> Smart queue
        </span>
        {data && (
          <Badge variant="outline" className="text-[9px]" data-testid="smart-queue-model">
            {data.model === 'tfidf+logreg'
              ? `learning from ${data.trained_on} of your decisions`
              : data.ai_priors_used
                ? `warm start from ${data.ai_priors_used} AI verdicts`
                : 'ranking by similarity to your question'}
          </Badge>
        )}
        <div className="flex-1" />
        <Button size="sm" variant="ghost" className="h-7 text-[11px]"
          onClick={async () => {
            if (!window.confirm('Reset the AI learning history for this session? Your include/exclude decisions are kept.')) return;
            try {
              await thesisApi.resetScreeningHistory(userId, sessionId);
              toast.success('Learning history cleared — the queue will rebuild');
              load();
            } catch (e) { toast.error(e.response?.data?.detail || e.message); }
          }}
          title="Clear the active-learning ordering history (decisions are kept)"
          data-testid="smart-queue-reset-history">
          Reset learning
        </Button>
        <Button size="sm" variant="ghost" className="h-7" onClick={() => load()}
          data-testid="smart-queue-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Training on your decisions…
        </div>
      )}

      {data?.seeding && data?.ai_status === 'running' && (
        <p className="text-[11px] text-muted-foreground" data-testid="smart-queue-seeding">
          Cold start: AI screening is labelling {data.ai_total} records to warm up the model
          ({data.ai_progress}/{data.ai_total}). You can start deciding right away — the ranking
          improves with every click.
        </p>
      )}

      {s && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid="smart-queue-stats">
            {[['Screened', `${s.reviewed}/${s.total}`], ['Includes found', s.included],
              ['Est. recall', `${s.estimated_recall_percent}%`],
              ['Work left', `${s.pending}`]].map(([label, value]) => (
              <div key={label} className="p-1.5 rounded border border-border bg-background">
                <div className="text-[15px] font-semibold tabular-nums">{value}</div>
                <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>

          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-[#1d4ed8] transition-all duration-700"
              style={{ width: `${Math.max(s.screened_percent, 2)}%` }}
              data-testid="smart-queue-progress" />
          </div>

          {curve.length > 3 && (
            <svg viewBox="0 0 300 46" className="w-full h-12" data-testid="smart-queue-curve">
              <polyline fill="none" stroke="#1d4ed8" strokeWidth="1.6"
                points={curve.map((p, i) =>
                  `${(300 * i) / Math.max(1, curve.length - 1)},${44 - (40 * p.included) / maxInc}`).join(' ')} />
              <line x1="0" y1="45" x2="300" y2="45" stroke="currentColor" strokeOpacity="0.15" />
            </svg>
          )}

          <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            <span>Stop rule: tell me after</span>
            {STOP_OPTIONS.map((n) => (
              <button key={n} type="button" onClick={() => setStopRule(n)}
                className={`px-1.5 py-0.5 rounded border ${s.stop_after === n
                  ? 'border-[#1d4ed8] text-[#1d4ed8] font-medium' : 'border-border'}`}
                data-testid={`smart-queue-stop-${n}`}>{n}</button>
            ))}
            <span>consecutive irrelevant · currently {s.consecutive_irrelevant}</span>
          </div>

          {s.stop_recommended && (
            <div className="p-2.5 rounded border border-amber-500/50 bg-amber-500/[0.07] text-[12px] flex gap-2"
              data-testid="smart-queue-stop-banner">
              <OctagonAlert className="w-4 h-4 text-amber-600 shrink-0" />
              <span>
                You can stop here. {s.consecutive_irrelevant} consecutive irrelevant records after
                screening {s.screened_percent}% of the set; the model expects only about{' '}
                <strong>{s.estimated_remaining_relevant}</strong> relevant records left
                (≈{s.estimated_recall_percent}% recall). Report this stopping rule in your Methods.
              </span>
            </div>
          )}
        </>
      )}

      {!loading && !top && (
        <p className="text-[12px] text-muted-foreground" data-testid="smart-queue-empty">
          Every record in this session has your decision. Nothing left to screen.
        </p>
      )}

      {top && (
        <div className="p-3 rounded-lg border border-border bg-background space-y-2"
          data-testid={`smart-queue-top-${top.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="text-[10px] bg-[#1d4ed8] hover:bg-[#1d4ed8]"
              data-testid="smart-queue-score">{top.al_percent}% likely relevant</Badge>
            {top.decided_by === 'ai' && (
              <Badge variant="outline" className="text-[9px]">AI said {top.decision}</Badge>
            )}
            {top.year && <Badge variant="outline" className="text-[9px]">{top.year}</Badge>}
          </div>
          <p className="text-[13px] font-medium leading-snug">{top.title}</p>
          <p className="text-[11px] text-muted-foreground">
            {top.journal} {top.doi ? `· doi:${top.doi}` : ''} {top.pmid ? `· PMID ${top.pmid}` : ''}
          </p>
          {!!top.al_why?.length && (
            <p className="text-[11px] text-[#1d4ed8] dark:text-[#7aa9ff]">
              Matched on: {top.al_why.join(', ')}
            </p>
          )}
          {top.abstract && (
            <p className="text-[11.5px] leading-relaxed max-h-40 overflow-y-auto text-foreground/85">
              {top.abstract}
            </p>
          )}
          {top.reason && <p className="text-[11px] italic text-muted-foreground">AI: {top.reason}</p>}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" onClick={() => decide(top, 'included')} disabled={!!deciding}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
              data-testid={`smart-queue-include-${top.id}`}>
              {deciding === top.id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Check className="w-3.5 h-3.5 mr-1.5" />} Include
            </Button>
            <Button size="sm" variant="outline" onClick={() => decide(top, 'fulltext')}
              disabled={!!deciding} className="h-8"
              data-testid={`smart-queue-fulltext-${top.id}`}>
              <FileSearch className="w-3.5 h-3.5 mr-1.5" /> Need full text
            </Button>
            <Button size="sm" variant="outline" onClick={() => decide(top, 'excluded')}
              disabled={!!deciding} className="h-8 border-rose-500/50 text-rose-700 dark:text-rose-400"
              data-testid={`smart-queue-exclude-${top.id}`}>
              <X className="w-3.5 h-3.5 mr-1.5" /> Exclude
            </Button>
          </div>
        </div>
      )}

      {!!rest.length && (
        <div className="space-y-1" data-testid="smart-queue-upnext">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Up next</div>
          {rest.map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-[11px]">
              <span className="tabular-nums text-[#1d4ed8] w-9 shrink-0">{r.al_percent}%</span>
              <span className="truncate">{r.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
